import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
    getAssignmentConflicts, 
    getAggregatedConflictSummary,
    parseConflictString, 
    calculatePopupPosition 
} from '../../../../../utils/leaderUtils';
import { leaderApi } from '../../../../../api/leaderApi';
import { projectApi } from '../../../../../api/projectApi';

/**
 * Hook to manage the state and logic for the AssignmentPanel.
 * 
 * @param {Object} props - The props passed to AssignmentPanel
 * @returns {Object} State and handlers for the form
 */
export function useAssignmentForm({
    assignment,
    workers,
    vehicles,
    assignmentsForConflicts = [],
    absencesForConflicts = [],
    presetDate = null,
    isUndatedPreset = false,
    companyLoc = null,
    onResourcesChange,
    onDateRangeChange,
    kolonnen = [],
    isConflictDetectionEnabled = true,
    onSave,
    onDelete,
    onClose
}) {
    const isEdit = !!assignment;

    const [form, setForm] = useState({
        project_id: null,
        project_name: '',
        location: '',
        notes: '',
        status: 'aktiv',
        workers: [],
        vehicles: [],
        route_from_address: '',
        route_from_lat: null,
        route_from_lng: null,
        route_to_address: '',
        route_to_lat: null,
        route_to_lng: null,
        route_return_address: '',
        route_return_lat: null,
        route_return_lng: null,
        travel_time_to: '',
        travel_time_return: '',
        work_duration: '',
    });

    const [isResModalOpen, setIsResModalOpen] = useState(false);
    const [hoveredConflict, setHoveredConflict] = useState(null);
    const hoverCloseDelayRef = useRef(null);
    const [isUndated, setIsUndated] = useState(false);
    const [activePickerId, setActivePickerId] = useState(null);

    // Click-Outside zum Schließen des RangePickers
    useEffect(() => {
        if (!activePickerId) return;
        const handleOutsideClick = (e) => {
            if (!e.target.closest('.assignment-date-pill')) {
                setActivePickerId(null);
            }
        };
        window.addEventListener('mousedown', handleOutsideClick);
        return () => window.removeEventListener('mousedown', handleOutsideClick);
    }, [activePickerId]);
    const [attemptedSave, setAttemptedSave] = useState(false);
    const [entries, setEntries] = useState([]);
    const [liveConflicts, setLiveConflicts] = useState({ workers: {}, vehicles: {}, total: 0 });
    const [customReturn, setCustomReturn] = useState(false);
    const [routeSectionOpen, setRouteSectionOpen] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);

    // Helpers
    const formatTime = (mins) => {
        if (mins === '' || mins == null) return '';
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h}:${m.toString().padStart(2, '0')}`;
    };

    const parseTime = (str) => {
        if (!str || typeof str !== 'string') return 0;
        const normalized = str.replace(',', '.').trim();
        if (normalized.includes(':')) {
            const parts = normalized.split(':');
            const h = parseInt(parts[0], 10) || 0;
            const m = parseInt(parts[1], 10) || 0;
            return h * 60 + m;
        }
        const val = parseFloat(normalized);
        if (!isNaN(val)) return Math.round(val * 60);
        return 0;
    };

    const findAssociatedKolonneId = useCallback((resId, resType, currentWorkers, currentVehicles) => {
        const potentialKolonnen = (kolonnen || []).filter(k => 
            k.members?.some(m => String(m.resource_id) === String(resId) && m.type === resType)
        );
        
        if (potentialKolonnen.length === 0) return null;

        const activeKolonneIds = new Set([
            ...currentWorkers.map(w => String(w.kolonne_id)),
            ...currentVehicles.map(v => String(v.kolonne_id))
        ].filter(id => id && id !== 'null' && id !== 'undefined'));

        const match = potentialKolonnen.find(k => activeKolonneIds.has(String(k.id)));
        return match ? match.id : null;
    }, [kolonnen]);

    // Initial load / Sync
    useEffect(() => {
        if (assignment) {
            const f = {
                id: assignment.id,
                project_id: assignment.project_id || null,
                project_name: assignment.project_name || '',
                location: assignment.location || '',
                notes: assignment.notes || '',
                status: assignment.status === 'geplant' ? 'aktiv' : (assignment.status || 'aktiv'),
                workers: (assignment.workers || []).map(aw => {
                    const master = (workers || []).find(w => String(w.id) === String(aw.worker_id));
                    return { 
                        ...aw, 
                        name: master?.name || aw.worker_name || aw.name || 'Unbekannt',
                        kolonne_id: aw.kolonne_id || null 
                    };
                }),
                vehicles: (assignment.vehicles || []).map(av => ({
                    ...av,
                    kolonne_id: av.kolonne_id || null
                })),
                route_from_address: assignment.route_from_address || '',
                route_from_lat: assignment.route_from_lat ? parseFloat(assignment.route_from_lat) : null,
                route_from_lng: assignment.route_from_lng ? parseFloat(assignment.route_from_lng) : null,
                route_to_address: assignment.route_to_address || '',
                route_to_lat: assignment.route_to_lat ? parseFloat(assignment.route_to_lat) : null,
                route_to_lng: assignment.route_to_lng ? parseFloat(assignment.route_to_lng) : null,
                route_return_address: assignment.route_return_address || '',
                route_return_lat: assignment.route_return_lat ? parseFloat(assignment.route_return_lat) : null,
                route_return_lng: assignment.route_return_lng ? parseFloat(assignment.route_return_lng) : null,
                travel_time_to: assignment.travel_time_to != null ? formatTime(assignment.travel_time_to) : '',
                travel_time_return: assignment.travel_time_return != null ? formatTime(assignment.travel_time_return) : '',
                work_duration: assignment.work_duration != null ? formatTime(assignment.work_duration) : '',
            };
            
            if (f.status === 'geplant') f.status = 'aktiv';
            setForm(f);

            const startDt = assignment.start_datetime ? new Date(assignment.start_datetime) : null;
            const endDt = assignment.end_datetime ? new Date(assignment.end_datetime) : null;
            const isAllDay = assignment.is_all_day || assignment.all_day || !assignment.start_datetime || !assignment.end_datetime ||
                (startDt && startDt.getHours() === 0 && startDt.getMinutes() === 0 && (endDt && (endDt.getHours() === 0 || endDt.getHours() === 23)));

            const formatD = d => {
                if (!d) return '';
                const pad = n => String(n).padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            };
            const formatT = d => {
                if (!d) return '';
                const pad = n => String(n).padStart(2, '0');
                return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
            };
            let isMultiDay = false;
            if (startDt && endDt) {
                const sd = formatD(startDt);
                const ed = formatD(endDt);
                if (sd !== ed) isMultiDay = true;
            }
            setEntries([{
                id: 1,
                startDate: formatD(startDt),
                endDate: formatD(endDt),
                withTime: !isAllDay,
                startTime: isAllDay ? '' : formatT(startDt),
                endTime: isAllDay ? '' : formatT(endDt),
                isMultiDay: isMultiDay
            }]);
            setRouteSectionOpen(!!(assignment.route_to_address || assignment.travel_time_to));
            setCustomReturn(assignment.route_return_address !== assignment.route_from_address && !!assignment.route_return_address);
            setIsUndated(assignment.status === 'undatiert' || !assignment.start_datetime);
        } else {
            const startDate = presetDate ? new Date(presetDate) : new Date();
            const pad = n => String(n).padStart(2, '0');
            const sdString = `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`;
            const hasTime = startDate.getHours() !== 0 || startDate.getMinutes() !== 0;
            let startTimeStr = '';
            let endTimeStr = '';
            if (hasTime) {
                startTimeStr = `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}`;
                const endDt = new Date(startDate.getTime() + 60 * 60 * 1000);
                endTimeStr = `${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
            }
            setForm({
                project_id: null,
                project_name: '',
                location: '',
                notes: '',
                status: 'aktiv',
                workers: [],
                vehicles: [],
                route_from_address: companyLoc?.address || '',
                route_from_lat: companyLoc?.lat || null,
                route_from_lng: companyLoc?.lng || null,
                route_to_address: '',
                route_to_lat: null,
                route_to_lng: null,
                route_return_address: companyLoc?.address || '',
                route_return_lat: companyLoc?.lat || null,
                route_return_lng: companyLoc?.lng || null,
                travel_time_to: '',
                travel_time_return: '',
                work_duration: '',
            });
            setCustomReturn(false);
            setRouteSectionOpen(false);
            setEntries([{
                id: Date.now(),
                startDate: sdString,
                endDate: sdString,
                withTime: hasTime,
                startTime: startTimeStr,
                endTime: endTimeStr,
                isMultiDay: false
            }]);
            setIsUndated(isUndatedPreset || false);
        }
    }, [assignment, presetDate, isUndatedPreset, companyLoc, workers]);

    // Handlers
    const handleChange = useCallback((field, value) => {
        setForm(f => {
            const updated = { ...f, [field]: value };
            if (field === 'status') {
                if (value === 'undatiert') {
                    setIsUndated(true);
                } else {
                    setIsUndated(false);
                    // Ensure at least one entry has a valid date when switching away from undated
                    setEntries(prev => {
                        const hasDate = prev.some(ent => !!ent.startDate);
                        if (!hasDate) {
                            const today = new Date();
                            const pad = n => String(n).padStart(2, '0');
                            const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
                            return prev.map((ent, i) => 
                                i === 0 ? { ...ent, startDate: todayStr, endDate: todayStr, withTime: false } : ent
                            );
                        }
                        return prev;
                    });
                }
            }
            return updated;
        });
    }, []);

    const handleTimeBlur = (field, value) => {
        if (!value) return;
        const mins = parseTime(value);
        handleChange(field, formatTime(mins));
    };

    const adjustTime = (field, deltaMins) => {
        const currentMins = parseTime(form[field]) || 0;
        const newMins = Math.max(0, currentMins + deltaMins);
        handleChange(field, formatTime(newMins));
    };

    const handleProjectSelect = async (projectId, project) => {
        if (!projectId || !project) {
            setForm(f => ({ ...f, project_id: null, project_name: '' }));
            return;
        }

        // Grunddaten aktualisieren
        setForm(f => ({
            ...f,
            project_id: projectId,
            project_name: project.name,
            location: project.address || f.location,
            route_to_address: project.address || f.route_to_address
        }));

        try {
            // Ressourcen des Projekts laden
            const projUsers = await projectApi.getProjectUsers(projectId);
            
            if (projUsers && projUsers.length > 0) {
                // Konvertiere in workerIds und vehicleIds (Rolle "Fahrzeug" = Fahrzeug)
                const workerIds = projUsers.filter(u => u.role !== 'Fahrzeug' && u.worker_id).map(u => u.worker_id);
                const vehicleIds = projUsers.filter(u => u.role === 'Fahrzeug' && u.worker_id).map(u => u.worker_id);

                if (workerIds.length > 0 || vehicleIds.length > 0) {
                    handleResourceConfirm(workerIds, vehicleIds);
                }
            }
        } catch (err) {
            console.error('Fehler beim Laden der Projektressourcen:', err);
        }
    };

    const handleCalculateRoute = useCallback(async () => {
        const fromLat = form.route_from_lat;
        const fromLng = form.route_from_lng;
        const toLat = form.route_to_lat;
        const toLng = form.route_to_lng;
        const retLat = customReturn ? form.route_return_lat : fromLat;
        const retLng = customReturn ? form.route_return_lng : fromLng;
        
        if (!toLat || !toLng || !fromLat || !fromLng) return;
        
        setIsCalculating(true);
        try {
            const resTo = await leaderApi.calculateRoute({ from_lat: fromLat, from_lng: fromLng, to_lat: toLat, to_lng: toLng });
            const durationToMins = Math.round((resTo.duration_seconds || 0) / 60);
            handleChange('travel_time_to', formatTime(durationToMins));
            
            if (retLat && retLng) {
                const resRet = await leaderApi.calculateRoute({ from_lat: toLat, from_lng: toLng, to_lat: retLat, to_lng: retLng });
                const durationRetMins = Math.round((resRet.duration_seconds || 0) / 60);
                handleChange('travel_time_return', formatTime(durationRetMins));
            }
        } catch (e) {
            console.error('Route calculation err', e);
        } finally {
            setIsCalculating(false);
        }
    }, [form.route_from_lat, form.route_from_lng, form.route_to_lat, form.route_to_lng, form.route_return_lat, form.route_return_lng, customReturn, handleChange]);

    // Auto-Routing
    useEffect(() => {
        const timer = setTimeout(() => {
            handleCalculateRoute();
        }, 600);
        return () => clearTimeout(timer);
    }, [handleCalculateRoute]);

    const addEntry = () => {
        if (entries.length === 0) return;
        const last = entries[entries.length - 1];
        setEntries([...entries, { ...last, id: Date.now() }]);
    };

    const removeEntry = (id) => entries.length > 1 && setEntries(entries.filter(e => e.id !== id));

    const updateEntry = (id, field, value) => {
        setEntries(prev => prev.map(e => {
            if (e.id !== id) return e;
            const up = { ...e, [field]: value };
            if (field === 'withTime' && !value) { up.startTime = ''; up.endTime = ''; }
            else if (field === 'withTime' && value) { up.startTime = '08:00'; up.endTime = '16:00'; }
            if (field === 'startDate' && up.endDate < value) up.endDate = value;
            if (field === 'endDate' && value < up.startDate) up.endDate = up.startDate;
            if (field === 'isMultiDay' && !value) up.endDate = up.startDate;
            return up;
        }));
    };

    const handleClearAll = () => {
        if (window.confirm('Möchtest du wirklich alle zugewiesenen Mitarbeiter und Fahrzeuge entfernen?')) {
            setForm(f => ({ ...f, workers: [], vehicles: [] }));
        }
    };

    const handleResourceConfirm = (workerIds, vehicleIds) => {
        const currentWorkers = [...form.workers];
        const currentVehicles = [...form.vehicles];

        const newWorkers = workerIds.map(id => {
            const existing = currentWorkers.find(w => String(w.worker_id) === String(id));
            if (existing) return existing;
            
            let foundKolonneId = findAssociatedKolonneId(id, 'worker', currentWorkers, currentVehicles);

            if (!foundKolonneId) {
                kolonnen.forEach(k => {
                    if (k.members?.some(m => m.type === 'worker' && String(m.resource_id) === String(id))) {
                        const allMembersSelected = k.members.every(m => 
                            (m.type === 'worker' && workerIds.includes(m.resource_id)) || 
                            (m.type === 'vehicle' && vehicleIds.includes(m.resource_id))
                        );
                        if (allMembersSelected) foundKolonneId = k.id;
                    }
                });
            }

            const w = (workers || []).find(work => String(work.id) === String(id));
            return { 
                worker_id: id, 
                name: w?.name || '', 
                assignment_role: w?.role || 'Monteur', 
                kolonne_id: foundKolonneId 
            };
        });

        const newVehicles = vehicleIds.map(id => {
            const existing = currentVehicles.find(v => String(v.vehicle_id) === String(id));
            if (existing) return existing;
            
            let foundKolonneId = findAssociatedKolonneId(id, 'vehicle', currentWorkers, currentVehicles);

            if (!foundKolonneId) {
                kolonnen.forEach(k => {
                    if (k.members?.some(m => m.type === 'vehicle' && String(m.resource_id) === String(id))) {
                        const allMembersSelected = k.members.every(m => 
                            (m.type === 'worker' && workerIds.includes(m.resource_id)) || 
                            (m.type === 'vehicle' && vehicleIds.includes(m.resource_id))
                        );
                        if (allMembersSelected) foundKolonneId = k.id;
                    }
                });
            }

            return { vehicle_id: id, kolonne_id: foundKolonneId };
        });

        setForm(f => ({
            ...f,
            workers: newWorkers,
            vehicles: newVehicles
        }));
    };

    const removeKolonne = (kolonneId) => {
        setForm(f => ({
            ...f,
            workers: f.workers.filter(w => String(w.kolonne_id) !== String(kolonneId)),
            vehicles: f.vehicles.filter(v => String(v.kolonne_id) !== String(kolonneId))
        }));
    };

    const removeWorker = (workerId) => setForm(f => ({ ...f, workers: f.workers.filter(w => w.worker_id !== workerId) }));
    
    const toggleVehicle = (vehicleId) => setForm(f => {
        const exists = f.vehicles.find(v => v.vehicle_id === vehicleId);
        return { ...f, vehicles: exists ? f.vehicles.filter(v => v.vehicle_id !== vehicleId) : [...f.vehicles, { vehicle_id: vehicleId }] };
    });

    const handleDrop = (e) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        try {
            const data = JSON.parse(e.dataTransfer.getData('application/json'));
            if (data.type === 'worker' && !form.workers.find(w => String(w.worker_id) === String(data.id))) {
                const kolonneId = findAssociatedKolonneId(data.id, 'worker', form.workers, form.vehicles);
                setForm(f => ({
                    ...f,
                    workers: [...f.workers, { worker_id: data.id, name: data.name, assignment_role: data.role || 'Monteur', kolonne_id: kolonneId || null }]
                }));
            } else if (data.type === 'vehicle' && !form.vehicles.find(v => String(v.vehicle_id) === String(data.id))) {
                const kolonneId = findAssociatedKolonneId(data.id, 'vehicle', form.workers, form.vehicles);
                setForm(f => ({
                    ...f,
                    vehicles: [...f.vehicles, { vehicle_id: data.id, kolonne_id: kolonneId || null }]
                }));
            } else if (data.type === 'kolonne' && data.members) {
                setForm(f => {
                    let nextWorkers = [...f.workers];
                    let nextVehicles = [...f.vehicles];
                    data.members.forEach(m => {
                        if (m.type === 'worker') {
                            if (!nextWorkers.find(w => String(w.worker_id) === String(m.resource_id))) {
                                const master = (workers || []).find(w => String(w.id) === String(m.resource_id));
                                nextWorkers.push({ 
                                    worker_id: m.resource_id, 
                                    name: master?.name || 'Unbekannt', 
                                    assignment_role: master?.role || 'Monteur',
                                    kolonne_id: data.id 
                                });
                            }
                        } else if (m.type === 'vehicle') {
                            if (!nextVehicles.find(v => String(v.vehicle_id) === String(m.resource_id))) {
                                nextVehicles.push({ vehicle_id: m.resource_id, kolonne_id: data.id });
                            }
                        }
                    });
                    return { ...f, workers: nextWorkers, vehicles: nextVehicles };
                });
            }
        } catch (err) { }
    };

    const handleSave = () => {
        setAttemptedSave(true);
        if (!form.project_name.trim()) {
            setTimeout(() => {
                window.alert('Bitte füllen Sie alle Pflichtfelder aus (Projektname).');
            }, 10);
            return;
        }
        const basePayload = { ...form };
        delete basePayload.id; 
        
        basePayload.travel_time_to = parseTime(basePayload.travel_time_to);
        basePayload.travel_time_return = parseTime(basePayload.travel_time_return);
        basePayload.work_duration = parseTime(basePayload.work_duration);
        if (!customReturn) {
            basePayload.route_return_address = basePayload.route_from_address;
            basePayload.route_return_lat = basePayload.route_from_lat;
            basePayload.route_return_lng = basePayload.route_from_lng;
        }
        
        const rawPayloads = isUndated ? [{
            ...basePayload,
            start_datetime: null,
            end_datetime: null,
            all_day: true,
            is_all_day: true,
            status: 'undatiert'
        }] : entries.map(ent => {
            const isAllDay = !ent.withTime || (ent.startTime === '' && ent.endTime === '');
            const normalizedEndDate = ent.isMultiDay ? ent.endDate : ent.startDate;
            let startDt = !ent.startDate ? null : `${ent.startDate}T${ent.startTime || '00:00'}:00`;
            let endDt = !normalizedEndDate ? null : `${normalizedEndDate}T${ent.endTime || '23:59'}:00`;

            return {
                ...basePayload,
                start_datetime: startDt,
                end_datetime: endDt,
                all_day: isAllDay,
                is_all_day: isAllDay,
                status: (basePayload.status === 'geplant' || basePayload.status === 'undatiert') ? 'aktiv' : basePayload.status
            };
        });

        const payloads = rawPayloads.map((p, idx) => {
            if (isEdit && idx === 0) {
                return { ...p, id: assignment.id };
            }
            return p;
        });

        const hasConflicts = isConflictDetectionEnabled && (Object.keys(liveConflicts.workers).some(id => form.workers.some(fw => String(fw.worker_id) === id)) ||
                           Object.keys(liveConflicts.vehicles).some(id => form.vehicles.some(fv => String(fv.vehicle_id) === id)));
        if (hasConflicts) {
             const details = [
                 ...form.workers.filter(w => liveConflicts.workers[String(w.worker_id)]).map(fw => {
                     const master = workers.find(w => String(w.id) === String(fw.worker_id));
                     return `- ${master?.name || fw.name || 'Unbekannt'}: ${liveConflicts.workers[String(fw.worker_id)]}`;
                 }),
                 ...form.vehicles.filter(v => liveConflicts.vehicles[String(v.vehicle_id)]).map(fv => {
                     const master = vehicles.find(v => String(v.id) === String(fv.vehicle_id));
                     return `- ${master?.license_plate || master?.internal_name || 'Fahrzeug'}: ${liveConflicts.vehicles[String(fv.vehicle_id)]}`;
                 })
             ].join('\n');
             if (!window.confirm(`⚠️ Konflikte:\n\n${details}\n\nTrotzdem speichern?`)) return;
        }

        onSave(payloads.length === 1 ? payloads[0] : payloads);
    };

    // External notifications
    useEffect(() => {
        onResourcesChange?.({ workers: form.workers, vehicles: form.vehicles });
    }, [form.workers, form.vehicles, onResourcesChange]);

    useEffect(() => {
        if (!entries || entries.length === 0) return;
        const validStartDates = entries.map(e => e.startDate).filter(d => !!d).sort();
        const validEndDates = entries.map(e => e.endDate).filter(d => !!d).sort();
        const minStart = validStartDates[0];
        const maxEnd = validEndDates[validEndDates.length - 1];

        const firstEntry = entries.find(e => e.startDate === minStart);
        const lastEntry = entries.find(e => e.endDate === maxEnd);
        const isAllDay = entries.every(e => !e.withTime);

        if (isUndated) {
            onDateRangeChange?.({ 
                start: null, 
                end: null,
                isUndated: true
            });
        } else if (minStart && maxEnd) {
            onDateRangeChange?.({ 
                start: minStart, 
                end: maxEnd,
                startTime: firstEntry?.startTime || '00:00',
                endTime: lastEntry?.endTime || '23:59',
                isAllDay: isAllDay,
                isUndated: false
            });
        }
    }, [entries, onDateRangeChange, isUndated]);

    // Live Conflicts
    useEffect(() => {
        if (!isConflictDetectionEnabled || isUndated || entries.length === 0) {
            setLiveConflicts({ workers: {}, vehicles: {}, total: 0 });
            return;
        }
        const timer = setTimeout(() => {
            const entry = entries[0];
            const mockAssignment = {
                id: assignment?.id,
                start_datetime: `${entry.startDate}T${entry.startTime || '00:00'}:00`,
                end_datetime: `${entry.endDate}T${entry.endTime || '23:59'}:00`,
                all_day: !entry.withTime || (entry.startTime === '' && entry.endTime === ''),
                workers: form.workers,
                vehicles: form.vehicles
            };
            const conflicts = getAssignmentConflicts(mockAssignment, assignmentsForConflicts, absencesForConflicts, vehicles);
            setLiveConflicts(conflicts);
        }, 400);
        return () => clearTimeout(timer);
    }, [entries, form.workers, form.vehicles, assignmentsForConflicts, absencesForConflicts, assignment?.id, vehicles, isConflictDetectionEnabled, isUndated]);

    const handleConflictEnter = (e, messages, title) => {
        if (hoverCloseDelayRef.current) {
            clearTimeout(hoverCloseDelayRef.current);
            hoverCloseDelayRef.current = null;
        }

        const stringArray = Array.isArray(messages) ? messages : [messages];
        if (stringArray.length === 0) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const position = calculatePopupPosition(rect, 320, 400, null, { gap: 10 });
        
        setHoveredConflict({
            title,
            blocks: stringArray.map(parseConflictString).filter(Boolean),
            position
        });
    };

    const handleConflictLeave = () => {
        if (hoverCloseDelayRef.current) clearTimeout(hoverCloseDelayRef.current);
        hoverCloseDelayRef.current = setTimeout(() => {
            setHoveredConflict(null);
            hoverCloseDelayRef.current = null;
        }, 250);
    };

    return {
        form,
        setForm,
        handleChange,
        handleProjectSelect,
        handleTimeBlur,
        adjustTime,
        handleCalculateRoute,
        isResModalOpen,
        setIsResModalOpen,
        handleResourceConfirm,
        handleClearAll,
        removeKolonne,
        removeWorker,
        toggleVehicle,
        handleDrop,
        handleSave,
        attemptedSave,
        isUndated,
        entries,
        setEntries,
        addEntry,
        removeEntry,
        updateEntry,
        activePickerId,
        setActivePickerId,
        liveConflicts,
        hoveredConflict,
        handleConflictEnter,
        handleConflictLeave,
        customReturn,
        setCustomReturn,
        routeSectionOpen,
        setRouteSectionOpen,
        isCalculating,
        hoverCloseDelayRef,
        getTotalDuration: () => {
            const tt = parseTime(form.travel_time_to) || 0;
            const tr = parseTime(form.travel_time_return) || 0;
            const tw = parseTime(form.work_duration) || 0;
            return formatTime(tt + tr + tw) || '0:00';
        },
        conflictSummary: useMemo(() => 
            getAggregatedConflictSummary(form, liveConflicts, workers, vehicles, kolonnen),
            [form, liveConflicts, workers, vehicles, kolonnen]
        ),
        findAssociatedKolonneId
    };
}
